import type { CustomFieldDTO } from '@tempo-io/item-store-dto';
import type { CustomFieldTypes } from '@/common/api/types/customField';
import type { CustomerLookupEntry } from './types';

export interface PublishedCustomFieldOptions {
  customFieldType: CustomFieldTypes;
  options: Array<{ id: string | number; name: string; logoUrl?: string | null }>;
}

export interface PublishedFieldLookups {
  customFieldDefinitions: readonly CustomFieldDTO[];
  resolvedUsers?: Record<string, { name: string; pictureUrl: string | null }>;
  resolvedItems?: Record<string, { id: number; name: string; itemTypeId: number }>;
  customerLookup?: Record<string, CustomerLookupEntry>;
}

function optionsForField(
  field: CustomFieldDTO,
  lookups: PublishedFieldLookups,
): PublishedCustomFieldOptions['options'] | null {
  switch (field.type) {
    case 'FIELD_TYPE_PICKLIST':
    case 'FIELD_TYPE_MULTI_PICKLIST':
      return field.picklistOptions.map((option) => ({ id: String(option.id), name: option.name }));
    case 'FIELD_TYPE_USER':
    case 'FIELD_TYPE_MULTI_USER':
      return Object.entries(lookups.resolvedUsers ?? {}).map(([id, user]) => ({
        id,
        name: user.name,
      }));
    case 'FIELD_TYPE_ITEM':
    case 'FIELD_TYPE_MULTI_ITEM':
      return Object.entries(lookups.resolvedItems ?? {}).map(([id, item]) => ({
        id,
        name: item.name,
      }));
    case 'FIELD_TYPE_MULTI_CUSTOMER':
      return Object.entries(lookups.customerLookup ?? {}).map(([id, customer]) => ({
        id,
        name: customer.name,
        logoUrl: customer.logoUrl ?? null,
      }));
    default:
      return null;
  }
}

export function buildPublishedCustomFieldOptions(
  lookups: PublishedFieldLookups,
): Record<string, PublishedCustomFieldOptions> {
  const result: Record<string, PublishedCustomFieldOptions> = {};

  for (const field of lookups.customFieldDefinitions) {
    const options = optionsForField(field, lookups);
    if (!options) continue;
    result[String(field.id)] = {
      customFieldType: field.type.replace('FIELD_TYPE_', '') as CustomFieldTypes,
      options,
    };
  }

  return result;
}
